"use client";

import { FadeIn, FadeInStaggerItem, Stagger } from "@/components/ui/motion";
import { BarChart3, Building, GraduationCap, Landmark, MoveRight, Shirt, ShoppingBag, Stethoscope, Truck } from "lucide-react";
import Link from "next/link";

const industries = [
    {
        name: "HealthTech",
        icon: Stethoscope,
        desc: "HIPAA-compliant platforms, Telemedicine apps, and patient management systems.",
        color: "hover:text-red-500",
        bg: "hover:bg-red-50",
    },
    {
        name: "FinTech",
        icon: Landmark,
        desc: "Secure payment gateways, investment dashboards, and trading algorithms.",
        color: "hover:text-blue-500",
        bg: "hover:bg-blue-50",
    },
    {
        name: "E-commerce",
        icon: ShoppingBag,
        desc: "High-conversion stores, inventory management, and headless commerce architectures.",
        color: "hover:text-emerald-500",
        bg: "hover:bg-emerald-50",
    },
    {
        name: "Logistics",
        icon: Truck,
        desc: "Fleet tracking, route optimization, and supply chain visualization tools.",
        color: "hover:text-amber-500",
        bg: "hover:bg-amber-50",
    },
    {
        name: "EdTech",
        icon: GraduationCap,
        desc: "LMS platforms, interactive learning modules, and virtual classrooms.",
        color: "hover:text-purple-500",
        bg: "hover:bg-purple-50",
    },
    {
        name: "Real Estate",
        icon: Building,
        desc: "Property listing portals, VR tours, and CRM integration for agencies.",
        color: "hover:text-indigo-500",
        bg: "hover:bg-indigo-50",
    },
    {
        name: "Fashion & Retail",
        icon: Shirt,
        desc: "Trend analysis, virtual try-ons, and supply chain transparency.",
        color: "hover:text-pink-500",
        bg: "hover:bg-pink-50",
    },
    {
        name: "Data Analytics",
        icon: BarChart3,
        desc: "Business intelligence dashboards, predictive modeling, and data warehousing.",
        color: "hover:text-cyan-500",
        bg: "hover:bg-cyan-50",
    },
];

export default function Industries() {
    return (
        <section className="py-24 bg-transparent">
            <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
                <FadeIn>
                    <div className="flex flex-col md:flex-row justify-between items-end mb-16 gap-6">
                        <div className="max-w-xl">
                            <h2 className="text-3xl font-bold mb-4 tracking-tight text-gray-900">Domain Expertise</h2>
                            <p className="text-gray-500 text-lg">
                                We speak your language. Our engineering team has deep vertical expertise across rapidly evolving sectors.
                            </p>
                        </div>
                        <Link href="/contact" className="hidden md:flex items-center gap-2 font-bold hover:gap-3 transition-all">
                            Discuss Your Project <MoveRight className="w-4 h-4" />
                        </Link>
                    </div>
                </FadeIn>

                <Stagger className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
                    {industries.map((ind, i) => (
                        <FadeInStaggerItem key={i} className="h-full">
                            <div className="group relative h-full bg-white/30 hover:bg-white/50 backdrop-blur-lg p-8 rounded-[32px] border border-zinc-200/50 shadow-sm hover:shadow-md transition-all duration-300">
                                <div className="w-12 h-12 rounded-xl bg-gray-50/50 border border-zinc-200/40 flex items-center justify-center mb-6 transition-all duration-300 group-hover:bg-white group-hover:border-zinc-300/80 text-zinc-500 group-hover:text-black">
                                    <ind.icon className="w-6 h-6 transition-colors duration-300" />
                                </div>
                                <h3 className="text-lg font-bold mb-3 text-gray-900">{ind.name}</h3>
                                <p className="text-sm text-gray-500 leading-relaxed font-medium">
                                    {ind.desc}
                                </p>
                            </div>
                        </FadeInStaggerItem>
                    ))}
                </Stagger>
            </div>
        </section>
    );
}
